export type ScenarioName = 'qualification_scenario'
export type ClassIdentifier = ScenarioName | (string & {})
export type VariableType = 'string' | 'int' | 'boolean' | 'float' | 'array_string' | 'array_int'

export interface VoiceOption {
  name: string
  label_key?: string
  gender: 'female' | 'male'
  provider_id: string
  sample_path: string
  slug?: string
  default?: boolean
  flag?: string
  locale?: string
  model_id?: string
}

export type AvailableVoices = Record<string, VoiceOption[]>

export interface CustomerIntent {
  name: string
  description: string
  training_phrases?: string[]
  output: string
  created_at: string
  /** Internal marker for the system-owned qualification fallback intent. */
  is_no_match?: boolean
}

export interface GeneralInformation {
  agent_name: string
  agent_description?: string
  supported_languages: {
    code: string
    audio?: string
    default?: boolean
  }[]
  voice_ids?: Record<string, string>
}

export interface Inactivity {
  user_away_timeout: number
  max_retries_user_away: number
  re_engagement_description?: string
  inactivity_output: string
}

export interface CallDurationLimits {
  max_call_duration_seconds: number
  max_call_duration_speech?: string[]
  max_call_duration_output: string
  max_concurrent_calls?: number
}

export interface ConversationalFlow {
  has_welcome_message: boolean
  has_wrap_up_message: boolean
  replacements?: Record<string, string>
}

export interface Knowledge {
  document_ids?: string[]
  custom_output?: string
  custom_output_unsuccessful?: string
}

export interface Variable {
  name: string
  description: string
  type: VariableType
}

export interface Sampling {
  generation_model?: string
  temperature: number
  top_k: number
  top_p: number
  max_tokens?: number
}

export interface FlagsEnabled {
  ok: boolean
  confusion: boolean
  hostility: boolean
  dissatisfaction: boolean
  complaint: boolean
  vulgarity: boolean
  fraud: boolean
}

export interface Flags {
  detection_window: number
  enabled: FlagsEnabled
}

export interface Vad {
  min_speech_duration: number
  activation_threshold: number
}

export interface Tools {
  common: string[]
}

export interface Experimental {
  fsm?: boolean
  form_schema?: Array<{ name: string; description: string; type: string }>
  sampling?: Sampling
  flags?: Flags
  tools?: Tools
  features?: Record<string, string | number | boolean | null>
  contextual_prompt_override?: string
  additional_parameters?: Record<string, string>
  persona_uuid_override?: string
  persona_role_override?: string
  persona_run_tools_on_enter?: Record<string, Record<string, unknown>>
  persona_voice_ids_override?: Record<string, Record<string, unknown>>
}

export interface SpeechInterruptions {
  welcome: boolean
  wrap_up: boolean
  re_engagement: boolean
  max_duration: boolean
}

export interface VirtualAgentConfiguration {
  id?: string | null
  general_information: GeneralInformation
  conversational_flow: ConversationalFlow
  customer_intents: CustomerIntent[]
  inactivity: Inactivity
  call_duration_limits: CallDurationLimits
  knowledge?: Knowledge
  experimental?: Experimental
  external_tools?: ExternalToolsPayload
  enabled: boolean
  speech?: {
    replacements?: Record<string, string>
    interruptible_welcome?: boolean
    interruptible_wrap_up?: boolean
    interruptible_re_engagement?: boolean
    interruptible_max_duration?: boolean
  }
}

export interface DispatchMetadata {
  scenario?: {
    class_identifier: ClassIdentifier
    fsm?: boolean
    variables?: Variable[]
  }
  tools: {
    routing: null
    common?: string[]
  }
  scenarios: Array<{
    type: ClassIdentifier
    opening: string[] | null
    closing: string[] | null
    contextual_prompt?: string
    entry_point_persona?: string
    personas: Record<
      string,
      {
        identifier?: string
        voice_ids?: Record<string, string>
        role?: string
        run_tools_on_enter?: Record<string, Record<string, unknown>>
      }
    >
    metadata?: Record<string, any>
  }>
  settings: {
    general_information: GeneralInformation
    conversational_flow: ConversationalFlow
    inactivity?: Inactivity
    call_duration_limits: CallDurationLimits
    knowledge?: Knowledge
    experimental?: Experimental
    external_tools?: ExternalToolsPayload
    enabled: boolean
    phone?: string
    sampling?: {
      temperature?: number
      top_k?: number
      top_p?: number
    }
    flags?: {
      detection_window?: number
      enabled?: string[]
    }
    features?: Record<string, string | number | boolean | null>
    contextual_prompt_override?: string
    additional_parameters?: Record<string, string>
    form_schema?: Array<{ name: string; description: string; type: string }>
    vad?: Vad
    created_at?: string
    created_by?: string
    updated_at?: string
    modified_by?: string
    last_modification?: string
    speech: {
      replacements: Record<string, string>
      language: string
      interruptible_welcome?: boolean
      interruptible_wrap_up?: boolean
      interruptible_re_engagement?: boolean
      interruptible_max_duration?: boolean
    }
  }
}

import type { ResourcePermissions } from './Permissions'

export type Configurator = {
  id: string
  name: string
  description: string
  phone: string | null
  created_at: string | null
  created_by: string | null
  last_modification: string | null
  modified_by: string | null
  language: string
  voice_flag?: string
  voice_locale?: string
  secondary_languages?: { flag: string; label: string }[]
  enabled: boolean
  permissions?: ResourcePermissions
}

export type ApiItem = {
  uid: string
  data: DispatchMetadata
  created_at: string
  updated_at: string
  created_by: string
  updated_by: string
}

export interface ConfiguratorPagination {
  page: number
  limit: number
  total_hits?: number
  total_pages?: number
  has_more?: boolean
  processing_time_ms?: number
}

export interface ConfiguratorApiResponse {
  configs: ApiItem[]
  total_hits: number
  offset: number
  limit: number
  page: number
  total_pages: number
  processing_time_ms?: number
  has_more?: boolean
}

export interface ConversationalFlowUI {
  has_welcome_message: boolean
  opening_phrases: { opening_phrase: string }[]
  has_wrap_up_message: boolean
  closing_phrases: { closing_phrase: string }[]
  replacements: { key: string; value: string }[]
}

export interface CallDurationLimitsUI {
  max_call_duration_seconds: number
  max_call_duration_speech: { message: string }[]
  max_call_duration_output: string
  max_concurrent_calls?: number
}

export interface VariableUI {
  name: string
  description: string
  type: VariableType
  fallbackValue?: string
}

export type McpToolCategory = 'read' | 'write'

export interface McpToolAnnotations {
  readOnlyHint?: boolean
  destructiveHint?: boolean
}

export interface McpServerHeader {
  key: string
  value: string
}

/** Tool entry in the UI state (per server). */
export interface McpTool {
  name: string
  description?: string
  inputSchema?: Record<string, unknown>
  annotations?: McpToolAnnotations
  enabled?: boolean
  description_override?: string
  category?: McpToolCategory
}

export interface McpServer {
  label: string
  url: string
  type: 'mcp'
  headers: McpServerHeader[]
  config_overrides?: Record<string, string>
  timeout_seconds: number
  tools: McpTool[]
}

export interface ExternalToolsUI {
  maxToolCallsPerTurn: number
  servers: McpServer[]
}

/** Backend wire format for an MCP tool inside a server entry. */
export interface McpToolPayload {
  name: string
  enabled?: false
  description_override?: string
  category?: McpToolCategory
  parameters?: Record<string, unknown>
}

export interface McpServerPayload {
  label: string
  url: string
  type: 'mcp'
  headers: Record<string, string>
  timeout_seconds: number
  tools: McpToolPayload[]
  [key: string]: unknown
}

export interface ExternalToolsPayload {
  max_tool_calls_per_turn: number
  servers: McpServerPayload[]
}

export type TranscriptionModel = '0226-Mini' | '0226-Medium' | 'asr-2026-ar'

export enum VoiceIsolationModel {
  Dtln = 'dtln',
  DeepFilterNet3 = 'deepfilternet3',
  None = 'none',
}

export interface ExperimentalUI {
  fsm: boolean
  speech_interruptions: SpeechInterruptions
  form_schema: VariableUI[]
  sampling: Sampling
  flags: Flags
  tools: {
    end_call: boolean
    append_to_session_memory: boolean
    text_to_numbers: boolean
    set_speech_language: boolean
    answer_with_rag: boolean
    qualify_call: boolean
    step_validation: boolean
    custom_skill: boolean
  }
  transcription_model: TranscriptionModel
  voice_isolation_model: VoiceIsolationModel
  noise_suppression_strength: number
  km_embedded_rag: boolean
  km_enable_rerank: boolean
  km_expand_query: boolean
  km_similarity_threshold: number
  km_number_of_doc_chunks: number
  vad: Vad
  feature_toggles: { key: string; value: string }[]
  contextual_prompt_override?: string
  additional_parameters: { key: string; value: string }[]
  mcp_instructions?: string
  class_identifier_override?: string
  persona_uuid_override: string
  persona_role_override: string
  persona_run_tools_on_enter: string
  persona_voice_ids_override: string
}
